import React from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { MdSearchOff, MdSearch } from "react-icons/md";

import styles from "./Main.module.scss";
import { useMain } from "./useMain";
import { SectionsNav } from "./components/sectionsNav/SectionsNav";
import { CollageBanner } from "../../common/components/collageBanner/CollageBanner";
import { AppSection } from "./components/appSection/AppSection";
import { Search } from "../../common/components/search/Search";
import { EmptyState } from "../../common/components/emptyState/EmptyState";
import {
  ITEMS_ROUTE,
  itemRoute,
  categoryRoute,
} from "../../common/utils/constants";
import { getClusterImageUrl } from "../../common/utils/functions/getImageUri";
import { selectCity } from "../../store/slices/citySlice";

export const Main: React.FC = () => {
  const selectedCity = useSelector(selectCity);
  const cityName = selectedCity?.value;

  const {
    clusters,
    query,
    setQuery,
    clearSearch,
    categories,
    items,
    bannerItems,
    isSearchMode,
    isBusy,
  } = useMain();

  const title = isSearchMode
    ? `Поиск: ${query} — ЛОГМОЛЛ`
    : cityName
      ? `ЛОГМОЛЛ — товары и бренды, ${cityName}`
      : "ЛОГМОЛЛ — товары и бренды";

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <div
        className={`${styles.MainContent} ${isSearchMode ? styles.Hidden : ""}`}
      >
        <CollageBanner items={bannerItems} />
      </div>

      <div
        className={`${styles.SearchWrap} ${isSearchMode ? styles.SearchActive : ""}`}
      >
        <Search
          value={query}
          onChange={setQuery}
          clear={clearSearch}
          placeHolder="Поиск товаров, компаний или категорий..."
        />
      </div>

      <div
        className={`${styles.MainContent} ${isSearchMode ? styles.Hidden : ""}`}
      >
        <SectionsNav />
        <AppSection />
      </div>

      {isSearchMode && (
        <div className={styles.ResultsWrap}>
          {isBusy ? (
            <div style={{ minHeight: 400 }} />
          ) : !query.trim() ? (
            <EmptyState
              icon={MdSearch}
              title="Введите запрос"
              description="Начните вводить название товара, компании или категории, чтобы найти нужное."
            />
          ) : (
            <div className={styles.ResultsList}>
              {categories.length > 0 && (
                <section className={styles.ResultSection}>
                  <h2 className={styles.ResultTitle}>Категории</h2>
                  <div className={styles.Grid}>
                    {categories.slice(0, 3).map((c) => (
                      <Link
                        key={c.id}
                        to={categoryRoute(c.id!)}
                        className={styles.Card}
                      >
                        <span className={styles.BgName}>{c.name}</span>
                        <h3 className={styles.Name}>{c.name}</h3>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {items.length > 0 && (
                <section className={styles.ResultSection}>
                  <h2 className={styles.ResultTitle}>Товары</h2>
                  <div className={styles.List}>
                    {items.slice(0, 5).map((item) => {
                      const cluster = clusters.find(
                        (c) => c.id === item.clusterId,
                      );
                      const price = item.prices?.[0]?.price;

                      return (
                        <Link
                          key={item.id}
                          to={itemRoute(item.id)}
                          className={styles.Row}
                        >
                          {item.files?.[0] && cluster && (
                            <img
                              src={getClusterImageUrl(cluster, item.files[0])}
                              alt={item.name}
                              className={styles.RowImage}
                            />
                          )}
                          <div className={styles.RowContent}>
                            <span
                              className={styles.RowBgName}
                              aria-hidden="true"
                            >
                              {item.name}
                            </span>
                            <div className={styles.RowMeta}>
                              <span className={styles.RowName}>
                                {item.name}
                              </span>
                              {price && (
                                <span className={styles.RowPrice}>
                                  {price} ₽
                                </span>
                              )}
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </section>
              )}

              {categories.length === 0 && items.length === 0 && (
                <EmptyState
                  icon={MdSearchOff}
                  title="Ничего не найдено"
                  description={`По запросу «${query}» ничего не найдено. Попробуйте изменить запрос.`}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
